import React from 'react';
import PropTypes from 'prop-types';
import {utils, writeFile} from 'xlsx';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {isEmpty, extractWorkSheetData, formatDate, ExcelDateToDateString} from "../../globals/GlobalFunctions";
import {GlobalConstants} from "../../globals/GlobalConstants";

let childSet = new Set();

//Set the type of the props data
export interface Props {
    excelData: any;
}

function getMemberObject() {
    return {
        memberDataCollection : {
            ssn :  "",
            essn: "",
            relationship: "",
            relationshipCode: "",
            insured: "",
            fn: "",
            mn: "",
            ln: "",
            prefix: "",
            suffix: "",
            dob: "",
            alt: "",
            uniq: "",
            add: "",
            add2: "",
            city: "",
            state: "",
            zip: "",
            country: "",
            hp: "",
            wp: "",
            fax: "",
            email: "",
            ethnicity: "",
            deathDate: "",
            sex: "",
            marital: "",
            handicap: "",
            empDate: "",
            termDate: "",
            enrollDate: "",
            disenrollDate: ""
        }
    }
}

function getEligibilityObject() {
    return {
        eligibilityDataCollection : {
            group: "",
            location: "",
            tier: "",
            planID: ""
        }
    }
}

function CoreToolFileFormatter({excelData}: Props) {
    //console.log(excelData, "DING");
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    try {
        //Check for data and that there is a sheet in the data named Census
        if (!isEmpty(excelData) && excelData.Sheets.CoreToolCensus !== undefined) {
            //console.log(excelData, "Made it to formatter!");

            //global function to extract the data into a sequential array of cell data values as well as the column and row ranges
            let sheetDataArray = extractWorkSheetData(excelData.Sheets.CoreToolCensus);

            let rawCellArray: any[] = sheetDataArray[0];

            let columnRange: number = sheetDataArray[1];

            let rowRange: number = sheetDataArray[2];

            //console.log(rawCellArray, "Raw Data");

            //we need to find the column index of the values we require, so we can grab the data for each row -- THIS IS WHERE COLUMN NAMES ARE SET
            //we add 1 for each for the loop multiplier to account for it being zero based, but later we subtract 1 to account for the very fist row actually starting at zero
            const memberIndexCollection = {
                ssnIndex: rawCellArray.indexOf('SSN') + 1,
                essnIndex: rawCellArray.indexOf('ESSN') + 1,
                RelationshipIndex: rawCellArray.indexOf('Relationship') + 1,
                fnIndex: rawCellArray.indexOf('First Name') + 1,
                mnIndex: rawCellArray.indexOf('Middle Name') + 1,
                lnIndex: rawCellArray.indexOf('Last Name') + 1,
                dobIndex: rawCellArray.indexOf('DOB') + 1,
                addIndex: rawCellArray.indexOf('Address') + 1,
                add2Index: rawCellArray.indexOf('Address 2') + 1,
                cityIndex: rawCellArray.indexOf('City') + 1,
                stateIndex: rawCellArray.indexOf('State') + 1,
                zipIndex: rawCellArray.indexOf('Zip Code') + 1,
                hpIndex: rawCellArray.indexOf('Home Phone') + 1,
                wpIndex: rawCellArray.indexOf('Work Phone') + 1,
                emailIndex: rawCellArray.indexOf('Email') + 1,
                ethnicityIndex: rawCellArray.indexOf('Ethnicity') + 1,
                deathDateIndex: rawCellArray.indexOf('Death Date') + 1,
                sexIndex: rawCellArray.indexOf('Sex') + 1,
                maritalIndex: rawCellArray.indexOf('Marital Status') + 1,
                handicapIndex: rawCellArray.indexOf('Handicap') + 1,
                empDateIndex: rawCellArray.indexOf('Hire Date') + 1,
                termDateIndex: rawCellArray.indexOf('Terminated Date') + 1,
                enrollDateIndex: rawCellArray.indexOf('Enrolled Date') + 1,
                disenrollDateIndex: rawCellArray.indexOf('Disenroll Date') + 1,
                countryIndex: rawCellArray.indexOf('Country') + 1,
            }

            //Same for the Eligibility Sheet
            const eligibilityIndexCollection = {
                groupIndex: rawCellArray.indexOf('Group ID') + 1,
                locationIndex: rawCellArray.indexOf('Location') + 1,
                tierIndex: rawCellArray.indexOf('Tier') + 1,
                tier1Index: rawCellArray.indexOf('Tier1') + 1,
                tier2Index: rawCellArray.indexOf('Tier2') + 1,
                tier3Index: rawCellArray.indexOf('Tier3') + 1,
                tier4Index: rawCellArray.indexOf('Tier4') + 1,
                tier5Index: rawCellArray.indexOf('Tier5') + 1,
                tier6Index: rawCellArray.indexOf('Tier6') + 1,
                tier7Index: rawCellArray.indexOf('Tier7') + 1,
                tier8Index: rawCellArray.indexOf('Tier8') + 1,
                tier9Index: rawCellArray.indexOf('Tier9') + 1,
                tier10Index: rawCellArray.indexOf('Tier10') + 1,
                tier11Index: rawCellArray.indexOf('Tier11') + 1,
                tier12Index: rawCellArray.indexOf('Tier12') + 1,
                tier13Index: rawCellArray.indexOf('Tier13') + 1,
                tier14Index: rawCellArray.indexOf('Tier14') + 1,
                tier15Index: rawCellArray.indexOf('Tier15') + 1,
                plan1Index: rawCellArray.indexOf('Plan1') + 1,
                plan2Index: rawCellArray.indexOf('Plan2') + 1,
                plan3Index: rawCellArray.indexOf('Plan3') + 1,
                plan4Index: rawCellArray.indexOf('Plan4') + 1,
                plan5Index: rawCellArray.indexOf('Plan5') + 1,
                plan6Index: rawCellArray.indexOf('Plan6') + 1,
                plan7Index: rawCellArray.indexOf('Plan7') + 1,
                plan8Index: rawCellArray.indexOf('Plan8') + 1,
                plan9Index: rawCellArray.indexOf('Plan9') + 1,
                plan10Index: rawCellArray.indexOf('Plan10') + 1,
                plan11Index: rawCellArray.indexOf('Plan11') + 1,
                plan12Index: rawCellArray.indexOf('Plan12') + 1,
                plan13Index: rawCellArray.indexOf('Plan13') + 1,
                plan14Index: rawCellArray.indexOf('Plan14') + 1,
                plan15Index: rawCellArray.indexOf('Plan15') + 1
            }

            //console.log(memberIndexCollection, "Index");

            //create the array of arrays that will become the sheet, and add the static headers
            let arrayOfMemberArrays: any[] = [["MemberUniqueID", "SubscriberSSN", "EmploymentStatus", "Relationship", "FirstName", "LastName", "MiddleName",
                "DateOfBirth", "MemberSSN", "AddressLine", "AddressLine2", "City", "State", "Zip", "HomePhone", "CellPhone",
                "Country", "Email", "Ethnicity", "DateOfDeath", "Gender", "MaritalStatus", "EmploymentDate", "TerminationDate", "GroupID", "LocationCode",
                "PlanCode", "CoverageTier", "PlanStartDate", "PlanEndDate"]];

            //we loop to create an array for each member using the rowRange to then push into the array above, with it ending each array based on the column length we got earlier
            for (let i = 1; i < rowRange; i++) {
                //our member to later be pushed. Oh, from what humble origins it begins
                let memberArray: any[] = [];
                //getting the object to which we assign our values as we traverse the array
                let memberObject = getMemberObject();
                //see above comment, but for eligibility
                let eligibilityObject = getEligibilityObject();
                //for each new member array we skip over the previous members determined by the column length, removing 1 to account for our zero based index at the beginning
                let rowMultiplier = columnRange * i - 1;

                //start with relationship, it and employee ssn are used to make the first 4 fields so they are paired together
                if (memberIndexCollection.RelationshipIndex > 0) {
                    memberObject.memberDataCollection.relationship = rawCellArray[(rowMultiplier + memberIndexCollection.RelationshipIndex)].toString().toUpperCase();
                }

                //Employee SSN - ensure it's a string, then remove dashes and cut off any garbage on the end
                if (memberIndexCollection.essnIndex > 0) {
                    if(rawCellArray[(rowMultiplier + memberIndexCollection.essnIndex)].toString().length > 0) {
                        memberObject.memberDataCollection.essn = rawCellArray[(rowMultiplier
                            + memberIndexCollection.essnIndex)].toString().replace(/-/g, "").replace(/\s/g, '').substring(0, 9).padStart(9, "0");
                    }
                }

                if (memberObject.memberDataCollection.essn) {
                    memberArray.push("", memberObject.memberDataCollection.essn.toString().replace(/-/g, ""));
                }
                else {
                    memberArray.push("", "");
                }

                memberArray.push("Active");

                if (memberObject.memberDataCollection.relationship) {
                    switch (memberObject.memberDataCollection.relationship) {
                        case "SELF":
                        case "EMPLOYEE":
                        case "EE":
                        case "01":
                        case "1":
                            memberArray.push("Employee");
                            break;
                        case "SPOUSE":
                        case "SP":
                        case "02":
                        case "2":
                        case "WIFE":
                        case "HUSBAND":
                            memberArray.push("Spouse");
                            break;
                        case "CHILD":
                        case "CH":
                        case "CHILD1":
                        case "CHILD2":
                        case "CHILD3":
                        case "CHILD4":
                        case "CHILD5":
                        case "CHILD6":
                        case "CHILD7":
                        case "CHILD8":
                        case "CHILD9":
                        case "CHILD10":
                        case "CHILD11":
                        case "CHILD12":
                        case "CHILD13":
                        case "CHILD14":
                        case "CHILD15":
                        case "03":
                        case "3":
                        case "SON":
                        case "DAUGHTER":
                            memberArray.push("Child");
                            break;
                        default:
                            memberArray.push("");
                    }
                }
                else {
                    memberArray.push("");
                }

                //first name
                if (memberIndexCollection.fnIndex > 0) {
                    memberObject.memberDataCollection.fn = rawCellArray[(rowMultiplier + memberIndexCollection.fnIndex)].toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.fn);
                }
                else {
                    memberArray.push("");
                }

                //last name
                if (memberIndexCollection.lnIndex > 0) {
                    memberObject.memberDataCollection.ln = rawCellArray[(rowMultiplier + memberIndexCollection.lnIndex)].toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.ln);
                }
                else {
                    memberArray.push("");
                }

                //middle name
                if (memberIndexCollection.mnIndex > 0) {
                    memberObject.memberDataCollection.mn = rawCellArray[(rowMultiplier + memberIndexCollection.mnIndex)].toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.mn);
                }
                else {
                    memberArray.push("");
                }

                //Date of Birth - converts excel dates or MM/DD/YYYY to YYYYMMDD
                if (memberIndexCollection.dobIndex > 0) {
                    memberObject.memberDataCollection.dob = rawCellArray[(rowMultiplier + memberIndexCollection.dobIndex)].toString();

                    if (memberObject.memberDataCollection.dob.includes("-")) {
                        memberObject.memberDataCollection.dob = memberObject.memberDataCollection.dob.substring(5, 7) + "/" + memberObject.memberDataCollection.dob.substring(8, 10) + "/" + memberObject.memberDataCollection.dob.substring(0, 4);
                    }

                    if (memberObject.memberDataCollection.dob.includes("/")) {
                        memberObject.memberDataCollection.dob = formatDate(memberObject.memberDataCollection.dob);
                    }
                    else if (memberObject.memberDataCollection.dob.length > 0 && memberObject.memberDataCollection.dob.length < 6){
                        memberObject.memberDataCollection.dob = ExcelDateToDateString(memberObject.memberDataCollection.dob);
                    }
                    memberArray.push(memberObject.memberDataCollection.dob.replace(/-/g, ""));
                }
                else {
                    memberArray.push("");
                }

                //Social security number - ensure it is a string, then filter dashes and cut off anything after
                if (memberIndexCollection.ssnIndex > 0) {
                    if(rawCellArray[(rowMultiplier + memberIndexCollection.ssnIndex)].toString().length > 0) {
                        memberObject.memberDataCollection.ssn = rawCellArray[(rowMultiplier
                            + memberIndexCollection.ssnIndex)].toString().replace(/-/g, "").replace(/\s/g, '').trim().substring(0, 9).padStart(9, 0);
                    }

                    memberArray.push(memberObject.memberDataCollection.ssn);

                }
                else {
                    memberArray.push("");
                }

                //Address
                if (memberIndexCollection.addIndex > 0) {
                    memberObject.memberDataCollection.add = rawCellArray[(rowMultiplier + memberIndexCollection.addIndex)].toString().toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.add);
                }
                else {
                    memberArray.push("");
                }

                //Address2
                if (memberIndexCollection.add2Index > 0) {
                    memberObject.memberDataCollection.add2 = rawCellArray[(rowMultiplier + memberIndexCollection.add2Index)].toString().toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.add2);
                }
                else {
                    memberArray.push("");
                }

                //City
                if (memberIndexCollection.cityIndex > 0) {
                    memberObject.memberDataCollection.city = rawCellArray[(rowMultiplier + memberIndexCollection.cityIndex)].toString().toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.city);
                }
                else {
                    memberArray.push("");
                }

                //State
                if (memberIndexCollection.stateIndex > 0) {
                    memberObject.memberDataCollection.state = rawCellArray[(rowMultiplier + memberIndexCollection.stateIndex)].toString().toUpperCase();
                    if(memberObject.memberDataCollection.state.length > 2 && GlobalConstants.statesWithAbbreviations.has(memberObject.memberDataCollection.state)){
                        memberObject.memberDataCollection.state = GlobalConstants.statesWithAbbreviations.get(memberObject.memberDataCollection.state)!;
                    }
                    memberArray.push(memberObject.memberDataCollection.state);
                }
                else {
                    memberArray.push("");
                }

                //Zip Code - Ensure it is a string, then trim off the extra digits
                if (memberIndexCollection.zipIndex > 0) {
                    if(rawCellArray[(rowMultiplier + memberIndexCollection.zipIndex)].toString().length > 0) {
                        memberObject.memberDataCollection.zip = rawCellArray[(rowMultiplier + memberIndexCollection.zipIndex)].toString().substring(0, 5).padStart(5, "0");
                    }
                    memberArray.push(memberObject.memberDataCollection.zip);
                }
                else {
                    memberArray.push("");
                }

                //Home Phone - ensure it is a string, then filter out parenthesis, spaces, dashes
                if (memberIndexCollection.hpIndex > 0) {
                    memberObject.memberDataCollection.hp = rawCellArray[(rowMultiplier + memberIndexCollection.hpIndex)].toString().replace(/[()\\.\s-]/g, "").slice(-10);
                    memberArray.push(memberObject.memberDataCollection.hp);
                }
                else {
                    memberArray.push("");
                }

                //Work Phone, same as home phone
                if (memberIndexCollection.wpIndex > 0) {
                    memberObject.memberDataCollection.wp = rawCellArray[(rowMultiplier + memberIndexCollection.wpIndex)].toString().replace(/[()\\.\s-]/g, "").slice(-10);
                    memberArray.push(memberObject.memberDataCollection.wp);
                }
                else {
                    memberArray.push("");
                }

                //Fax, same as phones
                if (memberIndexCollection.countryIndex > 0) {
                    memberObject.memberDataCollection.country = rawCellArray[(rowMultiplier + memberIndexCollection.countryIndex)].toString().replace(/[()\\.\s-]/g, "");
                    memberArray.push(memberObject.memberDataCollection.country);
                }
                else {
                    memberArray.push("");
                }

                //Email
                if (memberIndexCollection.emailIndex > 0) {
                    memberObject.memberDataCollection.email = rawCellArray[(rowMultiplier + memberIndexCollection.emailIndex)].toUpperCase();
                    memberArray.push(memberObject.memberDataCollection.email);
                }
                else {
                    memberArray.push("");
                }

                //Ethnicity Code, almost always blank
                if (memberIndexCollection.ethnicityIndex > 0) {
                    memberObject.memberDataCollection.ethnicity = rawCellArray[(rowMultiplier + memberIndexCollection.ethnicityIndex)];
                    memberArray.push(memberObject.memberDataCollection.ethnicity);
                }
                else {
                    memberArray.push("");
                }

                //Death Date - see date of birth
                if (memberIndexCollection.deathDateIndex > 0) {
                    memberObject.memberDataCollection.deathDate = rawCellArray[(rowMultiplier + memberIndexCollection.deathDateIndex)].toString();
                    if (memberObject.memberDataCollection.deathDate.includes("/")) {
                        memberObject.memberDataCollection.deathDate = formatDate(memberObject.memberDataCollection.deathDate);
                    }
                    else if (memberObject.memberDataCollection.deathDate.length > 0 && memberObject.memberDataCollection.deathDate.length < 6){
                        memberObject.memberDataCollection.deathDate = ExcelDateToDateString(memberObject.memberDataCollection.deathDate);
                    }
                    memberArray.push(memberObject.memberDataCollection.deathDate.replace(/-/g, ""));
                }
                else {
                    memberArray.push("");
                }

                //Sex - turns Male and Female into M and F
                if (memberIndexCollection.sexIndex > 0) {
                    memberObject.memberDataCollection.sex = rawCellArray[(rowMultiplier + memberIndexCollection.sexIndex)].toUpperCase().trim();
                    if(memberObject.memberDataCollection.sex === "MALE"){
                        memberObject.memberDataCollection.sex = "M";
                    }
                    else if(memberObject.memberDataCollection.sex === "FEMALE"){
                        memberObject.memberDataCollection.sex = "F";
                    }
                    else if(memberObject.memberDataCollection.sex === "UNKNOWN"){
                        memberObject.memberDataCollection.sex = "U";
                    }
                    memberArray.push(memberObject.memberDataCollection.sex);
                }
                else {
                    memberArray.push("");
                }

                //Marital Status but this is almost always blank
                if (memberIndexCollection.maritalIndex > 0) {
                    memberObject.memberDataCollection.marital = rawCellArray[(rowMultiplier + memberIndexCollection.maritalIndex)];
                    memberArray.push(memberObject.memberDataCollection.marital);
                }
                else {
                    memberArray.push("");
                }

                //Employed Date, see date of birth for date format explanation
                if (memberIndexCollection.empDateIndex > 0) {
                    memberObject.memberDataCollection.empDate = rawCellArray[(rowMultiplier + memberIndexCollection.empDateIndex)].toString();

                    if (memberObject.memberDataCollection.empDate.includes("-")) {
                        memberObject.memberDataCollection.empDate = memberObject.memberDataCollection.empDate.substring(5, 7) + "/" + memberObject.memberDataCollection.empDate.substring(8, 10) + "/" + memberObject.memberDataCollection.empDate.substring(0, 4);
                    }

                    if (memberObject.memberDataCollection.empDate.includes("/")) {
                        memberObject.memberDataCollection.empDate = formatDate(memberObject.memberDataCollection.empDate);
                    }
                    else if (memberObject.memberDataCollection.empDate.length > 0 && memberObject.memberDataCollection.empDate.length < 6){
                        memberObject.memberDataCollection.empDate = ExcelDateToDateString(memberObject.memberDataCollection.empDate);
                    }
                    if(memberObject.memberDataCollection.empDate.includes("NaN")){
                        memberArray.push("");
                    }
                    else {
                        memberArray.push(memberObject.memberDataCollection.empDate.replace(/-/g, ""));
                    }
                }
                else {
                    memberArray.push("");
                }

                //Termination Date, see above
                if (memberIndexCollection.termDateIndex > 0) {
                    memberObject.memberDataCollection.termDate = rawCellArray[(rowMultiplier + memberIndexCollection.termDateIndex)].toString();

                    if (memberObject.memberDataCollection.termDate.includes("-")) {
                        memberObject.memberDataCollection.termDate = memberObject.memberDataCollection.termDate.substring(5, 7) + "/" + memberObject.memberDataCollection.termDate.substring(8, 10) + "/" + memberObject.memberDataCollection.termDate.substring(0, 4);
                    }

                    if (memberObject.memberDataCollection.termDate.includes("/")) {
                        memberObject.memberDataCollection.termDate = formatDate(memberObject.memberDataCollection.termDate);
                    }
                    else if (memberObject.memberDataCollection.termDate.length > 0 && memberObject.memberDataCollection.termDate.length < 6){
                        memberObject.memberDataCollection.termDate = ExcelDateToDateString(memberObject.memberDataCollection.termDate);
                    }
                    if(memberObject.memberDataCollection.termDate.includes("NaN")){
                        memberArray.push("");
                    }
                    else {
                        memberArray.push(memberObject.memberDataCollection.termDate.replace(/-/g, ""));
                    }
                }
                else {
                    memberArray.push("");
                }

                //getting group ID
                if (eligibilityIndexCollection.groupIndex > 0) {
                    eligibilityObject.eligibilityDataCollection.group = rawCellArray[(rowMultiplier + eligibilityIndexCollection.groupIndex)].toString();
                    memberArray.push(eligibilityObject.eligibilityDataCollection.group);
                }
                else {
                    memberArray.push("");
                }

                //getting location/division
                if (eligibilityIndexCollection.locationIndex > 0) {
                    eligibilityObject.eligibilityDataCollection.location = rawCellArray[(rowMultiplier + eligibilityIndexCollection.locationIndex)].toString();
                    memberArray.push(eligibilityObject.eligibilityDataCollection.location.padStart(8, "0"));
                }
                else {
                    memberArray.push("");
                }

                //Enrollment Date, see above
                if (memberIndexCollection.enrollDateIndex > 0) {
                    memberObject.memberDataCollection.enrollDate = rawCellArray[(rowMultiplier + memberIndexCollection.enrollDateIndex)].toString();

                    if (memberObject.memberDataCollection.enrollDate.includes("-")) {
                        memberObject.memberDataCollection.enrollDate = memberObject.memberDataCollection.enrollDate.substring(5, 7) + "/" + memberObject.memberDataCollection.enrollDate.substring(8, 10) + "/" + memberObject.memberDataCollection.enrollDate.substring(0, 4);
                    }

                    if (memberObject.memberDataCollection.enrollDate.includes("/")) {
                        memberObject.memberDataCollection.enrollDate = formatDate(memberObject.memberDataCollection.enrollDate);
                    }
                    else if (memberObject.memberDataCollection.enrollDate.length > 0 && memberObject.memberDataCollection.enrollDate.length < 6){
                        memberObject.memberDataCollection.enrollDate = ExcelDateToDateString(memberObject.memberDataCollection.enrollDate);
                    }
                    memberObject.memberDataCollection.enrollDate = memberObject.memberDataCollection.enrollDate.replace(/-/g, "");
                }
                else {
                    memberObject.memberDataCollection.enrollDate = "";
                }

                //Disenroll Date, see above
                if (memberIndexCollection.disenrollDateIndex > 0) {
                    memberObject.memberDataCollection.disenrollDate = rawCellArray[(rowMultiplier + memberIndexCollection.disenrollDateIndex)].toString();

                    if (memberObject.memberDataCollection.disenrollDate.includes("-")) {
                        memberObject.memberDataCollection.disenrollDate = memberObject.memberDataCollection.disenrollDate.substring(5, 7) + "/" + memberObject.memberDataCollection.disenrollDate.substring(8, 10) + "/" + memberObject.memberDataCollection.disenrollDate.substring(0, 4);
                    }

                    if (memberObject.memberDataCollection.disenrollDate.includes("/")) {
                        memberObject.memberDataCollection.disenrollDate = formatDate(memberObject.memberDataCollection.disenrollDate);
                    }
                    else if (memberObject.memberDataCollection.disenrollDate.length > 0 && memberObject.memberDataCollection.disenrollDate.length < 6){
                        memberObject.memberDataCollection.disenrollDate = ExcelDateToDateString(memberObject.memberDataCollection.disenrollDate);
                    }
                    if(memberObject.memberDataCollection.disenrollDate.includes("NaN")){
                        memberObject.memberDataCollection.disenrollDate = ""
                    }
                    else {
                        memberObject.memberDataCollection.disenrollDate = memberObject.memberDataCollection.disenrollDate.replace(/-/g, "");
                    }
                }
                else {
                    memberObject.memberDataCollection.disenrollDate = "";
                }

                //console.log(memberArray);

                //make an array of the indexes of plans for looping iterations
                let planIndexArray = [eligibilityIndexCollection.plan1Index, eligibilityIndexCollection.plan2Index, eligibilityIndexCollection.plan3Index,
                    eligibilityIndexCollection.plan4Index, eligibilityIndexCollection.plan5Index, eligibilityIndexCollection.plan6Index, eligibilityIndexCollection.plan7Index,
                    eligibilityIndexCollection.plan8Index, eligibilityIndexCollection.plan9Index, eligibilityIndexCollection.plan10Index, eligibilityIndexCollection.plan11Index,
                    eligibilityIndexCollection.plan12Index, eligibilityIndexCollection.plan13Index, eligibilityIndexCollection.plan14Index, eligibilityIndexCollection.plan15Index];

                let tierIndexArray = [eligibilityIndexCollection.tier1Index, eligibilityIndexCollection.tier2Index, eligibilityIndexCollection.tier3Index,
                    eligibilityIndexCollection.tier4Index, eligibilityIndexCollection.tier5Index, eligibilityIndexCollection.tier6Index, eligibilityIndexCollection.tier7Index,
                    eligibilityIndexCollection.tier8Index, eligibilityIndexCollection.tier9Index, eligibilityIndexCollection.tier10Index, eligibilityIndexCollection.tier11Index,
                    eligibilityIndexCollection.tier12Index, eligibilityIndexCollection.tier13Index, eligibilityIndexCollection.tier14Index, eligibilityIndexCollection.tier15Index];

                //bool for the loop, goes false once a plan is not found, or it exceeds the 15
                let hasPlan: boolean = true;

                //iterator we increment up
                let j: number = 0;

                //used a do while loop, so it checks at least once for a plan then keeps going until it can't find one
                do {
                    if (planIndexArray[j] > 0) {
                        //get plan ID
                        eligibilityObject.eligibilityDataCollection.planID = rawCellArray[(rowMultiplier + planIndexArray[j])];

                        //Tier - tried to catch whatever commonly shows up here for generating tier but can always add more, still not sure if I should do a default action
                        if (eligibilityIndexCollection.tierIndex > 0) {
                            eligibilityObject.eligibilityDataCollection.tier = rawCellArray[(rowMultiplier + eligibilityIndexCollection.tierIndex)].toUpperCase();
                            switch (eligibilityObject.eligibilityDataCollection.tier) {
                                case "EMPLOYEE":
                                case "EMPLOYEE ONLY":
                                case "SELF":
                                case "EMP":
                                case "EE":
                                case "EM":
                                    eligibilityObject.eligibilityDataCollection.tier = "EMP";
                                    break;
                                case "SPOUSE":
                                case "EMPLOYEE + SPOUSE":
                                case "EMPLOYEE AND SPOUSE":
                                case "ESP":
                                case "ES":
                                    eligibilityObject.eligibilityDataCollection.tier = "ESP";
                                    break;
                                case "CHILDREN":
                                case "EMPLOYEE + CHILDREN":
                                case "EMPLOYEE + CHILD(REN)":
                                case "EMPLOYEE + CHILD":
                                case "EMPLOYEE AND CHILDREN":
                                case "CHILD(REN)":
                                case "CHILD":
                                case "ECH":
                                case "EC":
                                    eligibilityObject.eligibilityDataCollection.tier = "ECH";
                                    break;
                                case "FAMILY":
                                case "EMPLOYEE + FAMILY":
                                case "EMPLOYEE AND FAMILY":
                                case "FAM":
                                case "EF":
                                    eligibilityObject.eligibilityDataCollection.tier = "FAM";
                                    break;
                                default:
                                //if I need a catch
                            }
                        } else {
                            eligibilityObject.eligibilityDataCollection.tier = "";
                        }

                        if(tierIndexArray[j] > 0) {
                            eligibilityObject.eligibilityDataCollection.tier = rawCellArray[(rowMultiplier + tierIndexArray[j])].toUpperCase();
                            switch (eligibilityObject.eligibilityDataCollection.tier) {
                                case "EMPLOYEE":
                                case "EMPLOYEE ONLY":
                                case "SELF":
                                case "EMP":
                                case "EE":
                                case "EM":
                                    eligibilityObject.eligibilityDataCollection.tier = "EMP";
                                    break;
                                case "SPOUSE":
                                case "EMPLOYEE + SPOUSE":
                                case "EMPLOYEE AND SPOUSE":
                                case "ESP":
                                case "ES":
                                    eligibilityObject.eligibilityDataCollection.tier = "ESP";
                                    break;
                                case "CHILDREN":
                                case "EMPLOYEE + CHILDREN":
                                case "EMPLOYEE + CHILD(REN)":
                                case "EMPLOYEE + CHILD":
                                case "EMPLOYEE AND CHILDREN":
                                case "CHILD(REN)":
                                case "CHILD":
                                case "ECH":
                                case "EC":
                                    eligibilityObject.eligibilityDataCollection.tier = "ECH";
                                    break;
                                case "FAMILY":
                                case "EMPLOYEE + FAMILY":
                                case "EMPLOYEE AND FAMILY":
                                case "FAM":
                                case "EF":
                                    eligibilityObject.eligibilityDataCollection.tier = "FAM";
                                    break;
                                default:
                                //if I need a catch
                            }
                        }

                        //If there is a plan ID make an eligibility record with the info you have
                        if (eligibilityObject.eligibilityDataCollection.planID && eligibilityObject.eligibilityDataCollection.planID.length > 0) {
                            
                            let memberCopy = [...memberArray];

                            memberCopy.push(eligibilityObject.eligibilityDataCollection.planID,
                                eligibilityObject.eligibilityDataCollection.tier, memberObject.memberDataCollection.enrollDate, memberObject.memberDataCollection.disenrollDate);
                            //push the eligibility record into the array of arrays
                            arrayOfMemberArrays.push(memberCopy);
                            //and reset the plan ID for the next ID
                            eligibilityObject.eligibilityDataCollection.planID = "";
                            eligibilityObject.eligibilityDataCollection.tier = "";
                        }
                        //increment
                        j++;
                        //if more than 15, idk man that's too much insurance for one person to handle
                        if (j > 14) {
                            hasPlan = false;
                        }
                    }
                    //plan not found
                    else {
                        hasPlan = false;
                    }


                }
                while (hasPlan);
                //arrayOfMemberArrays.push(memberArray);
            }

            //clear our hashset for children member IDs after the set has been completed
            childSet.clear();

            //console.log("member sheet", arrayOfMemberArrays);
            //console.log("elig sheet", arrayOfEligibilityArrays);

            //create our workbook
            const censusWorkbook = utils.book_new();
            //make the member sheet from our member array of arrays
            let ws1 = utils.aoa_to_sheet(arrayOfMemberArrays);
            //add it to the new workbook
            utils.book_append_sheet(censusWorkbook, ws1, "Sheet1");

            //it exists and has a sheet send it back to app for export
            if (censusWorkbook && censusWorkbook.SheetNames.includes("Sheet1")) {
                if(!censusWorkbook.Props) censusWorkbook.Props = {};

                //setting the workbook title through props
                censusWorkbook.Props.Title = "VBA_CoreToolCensus_" + excelData.FileName + ".xlsx";

                //writing the file which will make the download, second param is the name of the file and third is required options
                writeFile(censusWorkbook, "VBA_CoreToolCensus_" + excelData.FileName + ".xlsx",{});

                console.log("File Export Complete!");
                setSuccessAlert(true);
            }
            else {
                setErrorAlert(true);
                console.log("Unknown Export Error!");
            }

            //clear it out at the end in case of rerendering
            excelData.Sheets.CoreToolCensus = undefined;
        }
    }
    catch(e) {
        console.log("ERROR", e);

        setErrorAlert(true);
        //clear it out after errors in case of rerendering otherwise the error spams will continue until morale improves
        excelData.Sheets.CoreToolCensus = undefined;
    }
    return (
        <div>
            {successAlert ? <Alert severity='success' onClose={() => setSuccessAlert(false)} variant="filled">
                <AlertTitle>Success!</AlertTitle>
                File Download Complete!
            </Alert> : <></> }
            {errorAlert ? <Alert severity='error' onClose={() => setErrorAlert(false)} variant="filled">
                <AlertTitle>Error!</AlertTitle>
                There was an error with this file!
            </Alert> : <></> }
        </div>
    );
}

export default React.memo(CoreToolFileFormatter)

CoreToolFileFormatter.propTypes = {
    excelData: PropTypes.object.isRequired
};